<template>
  <div
    class="ai-banner"
    :class="{
      'items-end': !isAiActive,
    }"
  >
    <template v-if="isAiActive">
      <div
        v-if="!hideBotDisclaimer"
        :class="`banner ${bannerType} flex flex-row items-center gap-2 border-b border-solid text-center text-xs font-normal`"
        :style="{
          background: $dm('#F6F7F8', '#27272A'),
          color: $dm('#5D6974', '#E4E4E7'),
          borderColor: $dm('#E5E7EB', '#3F3F46'),
        }"
      >
        <span class="banner__message flex-1">
          {{ bannerMessage }}
        </span>

        <span v-if="isManyNests" class="banner__nest flex-1">
          <select
            v-model="selectedAiNestIdComputed"
            class="w-full rounded border border-solid px-1 py-1 focus:outline-none"
            :style="{
              background: widgetColor,
              color: textColor,
            }"
          >
            <option
              v-for="aiNest in aiNests"
              :key="aiNest.id"
              :value="aiNest.id"
            >
              {{ aiNest.title }}
            </option>
          </select>
        </span>
      </div>
    </template>

    <template v-if="!hideBotHandover">
      <div v-if="isAiActive" class="handover-ai-chat-button-container">
        <custom-button
          :text-color="textColor"
          :bg-color="widgetColor"
          @click="handoverAiChat"
        >
          <fluent-icon icon="person" :size="16" />
          {{ $t('AI_CHAT.BANNER_BUTTON') }}
        </custom-button>
      </div>

      <custom-button
        v-else
        class="enable-ai-chat-button"
        :style="{
          background: widgetColor,
        }"
        @click="handoverAiChat"
      >
        <fluent-icon icon="robot-face" :size="16" :color="textColor" />
      </custom-button>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { getContrastingTextColor } from '@chatwoot/utils';

import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import CustomButton from 'shared/components/Button';
import configMixin from '../mixins/configMixin';
import darkModeMixin from 'widget/mixins/darkModeMixin';

export default {
  name: 'AiBanner',
  components: { CustomButton, FluentIcon },
  mixins: [configMixin, darkModeMixin],
  data() {
    return {
      bannerType: 'success',
      selectedAiNestId: '',
    };
  },
  computed: {
    ...mapGetters({
      aiNests: 'chatbot/getAiNests',
      isInboxHasAi: 'chatbot/getIsInboxHasAi',
      isAiChatHandover: 'chatbot/getIsAiChatHandover',
      widgetColor: 'appConfig/getWidgetColor',
      conversationParams: 'conversationAttributes/getConversationParams',
      appConfig: 'appConfig/getAppConfig',
      hideBotDisclaimer: 'appConfig/getHideBotDisclaimer',
      hideBotHandover: 'appConfig/getHideBotHandover',
    }),

    selectedAiNestIdComputed: {
      get() {
        return this.selectedAiNestId;
      },

      async set(newValue) {
        const oldValue = this.selectedAiNestId;

        try {
          const newAiNest = this.selectAiNestById(newValue);

          if (!newAiNest.nestId) {
            throw new Error();
          }

          this.selectedAiNestId = newValue;
          await this.changeAiNest(newAiNest.nestId);
        } catch (error) {
          this.$nextTick(() => {
            this.selectedAiNestId = oldValue;
          });
        }
      },
    },

    // ------------------------------------------- //

    textColor() {
      return getContrastingTextColor(this.widgetColor);
    },

    currentRoute() {
      return this.$route.name;
    },

    isAiActive() {
      if (!this.isInboxHasAi) {
        return false;
      }

      return !this.isAiChatHandover;
    },

    isManyNests() {
      return this.aiNests?.length > 1;
    },

    bannerMessage() {
      if (this.appConfig.botDisclaimer) {
        return this.appConfig.botDisclaimer;
      }

      return this.isManyNests
        ? this.$t('AI_CHAT.MANY_NESTS_BANNER_TEXT')
        : this.$t('AI_CHAT.BANNER_TEXT');
    },
  },
  watch: {
    aiNests: {
      handler(newValue) {
        this.selectedAiNestId = newValue[0]?.id || '';
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async handoverAiChat() {
      await this.$store.dispatch('chatbot/handoverAiChat', {
        sourceId: this.sourceIdAndInboxId.source_id,
      });
    },

    async changeAiNest(nestId) {
      await this.$store.dispatch('chatbot/changeAiNest', {
        sourceId: this.sourceIdAndInboxId.source_id,
        nestId: nestId,
      });
    },

    // ------------------------------------------- //

    selectAiNestById(id) {
      return this.aiNests.find((aiNest) => aiNest.id === id);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~widget/assets/scss/variables.scss';
.ai-banner {
  @apply relative flex flex-col;

  .banner {
    padding: $space-slab;

    &.success {
      background: $color-success;

      .banner__nest {
        select {
          @apply border-gray-300;

          background-color: $color-success;
        }
      }
    }
    &.error {
      background: $color-error;

      .banner__nest {
        select {
          @apply border-red-300;

          background-color: $color-error;
        }
      }
    }
  }

  .handover-ai-chat-button-container {
    @apply flex justify-center p-2;

    button {
      @apply flex flex-row items-center gap-1 rounded border border-gray-200 text-xs font-medium;

      background: transparent;
      padding: 0.75rem 0.625rem;
      background-color: rgba(246, 247, 248, var(--bg-opacity));
    }
  }

  .enable-ai-chat-button {
    @apply relative rounded-l-md rounded-r-none;

    top: -50%;
    z-index: 100;
  }
}
</style>
